import { useUser } from "@clerk/nextjs";
import { useMatchingLibraries } from "features/libraries/hooks/useMatchingLibraries";
import useClerkMetadata from "features/User/hooks/useClerkMetadata";
import { Library } from "helpers/api";
import { useCallback, useEffect, useState } from "react";
import { useAppSelector } from "hooks/useStore";
import { useRouter } from "next/router";
import path from "constants/path";

/**
 * Hook to manage the institution selection modal state
 * Shows the modal when:
 * 1. User is signed in
 * 2. User has matching libraries based on email domain
 * 3. User hasn't already selected a library
 * 4. User is not in the onboarding flow
 */
export function useInstitutionSelectionModal() {
  const { isSignedIn, isLoaded: isUserLoaded } = useUser();
  const { userMetadata, isUserMetadataLoaded, updateUserMetadata } =
    useClerkMetadata();
  const {
    hasMatches,
    matchingLibraries,
    isLoading: isLibrariesLoading,
  } = useMatchingLibraries();
  const [showModal, setShowModal] = useState(false);
  const router = useRouter();

  // Check if there are any modals in the stack
  const hasModalsInStack = useAppSelector(
    (state) => state.modalStack.stack.length > 0
  );

  // Check if user is in onboarding flow
  const isOnboardingFlow = router.pathname.startsWith(path.SIGN_UP);

  useEffect(() => {
    if (!isUserLoaded || !isUserMetadataLoaded || isLibrariesLoading) {
      return;
    }

    const hasSeenLibraryModal = Boolean(
      userMetadata?.settings?.has_seen_library_modal
    );

    const shouldShowModal =
      isSignedIn &&
      hasMatches &&
      !hasSeenLibraryModal &&
      !userMetadata?.settings?.library_id &&
      !hasModalsInStack && // Don't show if other modals are open
      !isOnboardingFlow; // Don't show during onboarding

    setShowModal(shouldShowModal);
  }, [
    isSignedIn,
    isUserLoaded,
    isUserMetadataLoaded,
    isLibrariesLoading,
    hasMatches,
    userMetadata?.settings?.has_seen_library_modal,
    userMetadata?.settings?.library_id,
    hasModalsInStack,
    isOnboardingFlow,
  ]);

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleConfirm = useCallback(
    async (selectedLibrary: Library | null) => {
      const newSettings = { ...(userMetadata?.settings || {}) };
      if (selectedLibrary) {
        newSettings.library_id = selectedLibrary.library_id;
      }

      newSettings.has_seen_library_modal = true;
      await updateUserMetadata({
        settings: newSettings,
      });
      handleCloseModal();
    },
    [userMetadata, updateUserMetadata]
  );

  const handleCancel = useCallback(async () => {
    // Mark modal as seen without selecting an institution
    const newSettings = { ...(userMetadata?.settings || {}) };
    newSettings.has_seen_library_modal = true;
    await updateUserMetadata({
      settings: newSettings,
    });
    handleCloseModal();
  }, [userMetadata, updateUserMetadata]);

  return {
    showModal,
    handleConfirm,
    handleCancel,
    matchingLibraries,
  };
}
